/* eslint-disable @typescript-eslint/no-unused-vars */

import { FC, useRef, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { logEvent, PROJECT_EVENTS } from "../../../../utils/amplitude";
import { Box, Typography } from "@mui/material";
import deleteIcon from "../../../../images/DeleteIcon.png";
import EditIcon from "../../../../images/EditIcon.png";
import ViewIcon from "../../../../images/visible.png";
import { ColDiv } from "../../../../components/misc/misc.styled";
import { Container } from "../../../../globalstyled";
import { Spacer, TxtInput } from "../../../../components";
import LeadsTableHeaderCard from "../../../../components/table/CustomTableHeaderCard";
import LeadsTableBodyCard from "../../../../components/table/CustomTableBodyCard";
import SideDrawer from "../../ItemCreation/SideDrawer";
import SideDrawerHeader from "../../ItemCreation/SideDrawer/SideDrawerHeader";
import CancelAndSubmitButtons from "../../ItemCreation/ItemCreationComponent/SubmitAndCancelButton";
import Pagination from "../../ItemCreation/ItemCreationComponent/Pagination";
import TableViewDetails from "../../../../components/table/TableViewDetails";
import SearchAndCreateComponent from "../../ItemCreation/ItemCreationComponent/SearchAndCreateComponent";
import { useParams } from "react-router-dom";
import { useFetchNotes } from "../../../../apolloClient/hooks/itemCreation/fetchHooks";
import {
  useCreateNotes,
  useDeleteNotes,
  useEditNotes,
} from "../../../../apolloClient/hooks/itemCreation/createHooks";
import {
  CreateNoteInput,
  EditNoteInput,
} from "../../../../types/graphQlQuery.interface";
import { format } from "date-fns";
import { DeleteIcon } from "../../dashboard.styled";

interface Note {
  id?: number;
  title: string;
  createdAt: string;
  notes: string;
}

interface NotesProps {
  permissions?: boolean;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return format(date, "MMMM dd, yyyy, h:mm a");
};

export const Notes: FC<NotesProps> = ({ permissions }) => {
  let { id } = useParams();

  const { loading, error, notesData, refetch } = useFetchNotes({
    projectId: Number(id),
    limit: 100,
    offset: 0,
    // isSkip: isSkip,
  });

  const { createNotes } = useCreateNotes();
  const { editNotes } = useEditNotes();
  const { deleteNote } = useDeleteNotes();

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(2);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [currentNote, setCurrentNote] = useState<Note | null>(null);
  const [keyword, setKeyword] = useState("");
  const [viewRowDetailsData, setViewRowDetailsData] = useState<Note | null>(
    null
  );
  const [openViewDrawer, setOpenViewDrawer] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrawerOpen = () => {
    setCurrentNote(null);
    setIsEditMode(false);
    setOpenDrawer(true);
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
  };

  const handleEditButtonClick = (note: Note) => {
    setCurrentNote(note);
    setIsEditMode(true);
    setOpenDrawer(true);
  };

  const handleCreateNote = async () => {
    if (!currentNote?.title || !currentNote?.notes) {
      toast("Please fill in all fields", { type: "error" });
      return;
    }

    const input: CreateNoteInput = {
      projectId: Number(id),
      title: currentNote.title,
      notes: currentNote.notes,
    };

    const success = await createNotes(input);
    if (success) {
      toast("Note added successfully.", { type: "success" });
      
      // Track note creation
      logEvent(PROJECT_EVENTS.NOTE_CREATED, { 
        project_id: String(id), 
        note_title: currentNote.title 
      });
      
      refetch();
      handleDrawerClose();
    } else {
      console.log("Failed to add note.");
    }
  };

  const handleUpdateNote = async () => {
    if (!currentNote?.title || !currentNote?.notes) {
      toast("Please fill in all fields", { type: "error" });
      return;
    }

    const input: EditNoteInput = {
      Id: Number(currentNote.id),
      title: currentNote.title,
      notes: currentNote.notes,
    };

    const success = await editNotes(input);
    if (success) {
      toast("Note updated successfully.", { type: "success" });
      refetch();
      handleDrawerClose();
    } else {
      console.log("Failed to update note.");
    }
  };

  const handleSaveNote = async () => {
    if (isEditMode) {
      await handleUpdateNote();
    } else {
      await handleCreateNote();
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    const success = await deleteNote(noteId);
    if (success) {
      toast("Note deleted successfully.", { type: "error" });
      refetch();
    } else {
      console.log("Failed to delete note.");
    }
  };

  const goToPreviousPage = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  const goToNextPage = () => {
    setPage((prevPage) =>
      Math.min(prevPage + 1, Math.ceil(notesData.length / rowsPerPage) - 1)
    );
  };

  const handleSearchIconClick = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleViewNote = (note: Note) => {
    const formattedNote = { ...note, createdAt: formatDate(note.createdAt) };
    setViewRowDetailsData(formattedNote);
    setOpenViewDrawer(true);
  };

  const handleViewDrawerClose = () => {
    setOpenViewDrawer(false);
  };

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  const filteredNotes = notesData.filter((note) =>
    keyword
      ? note.title.toLowerCase().includes(keyword.toLowerCase()) ||
        note.notes.toLowerCase().includes(keyword.toLowerCase())
      : true
  );

  const NotesTable = () => (
    <Box sx={{ width: "100%" }}>
      {/* Header with Search and Create */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          marginBottom: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            padding: "8px 12px",
            flex: { xs: 1, sm: "0 1 400px" },
          }}
        >
          <Box
            component="img"
            src={require("../../../../images/searchIcon.png")}
            sx={{
              width: 18,
              height: 18,
              marginRight: 1,
              cursor: "pointer",
            }}
            onClick={handleSearchIconClick}
          />
          <TxtInput
            placeholder="Search Notes"
            onChange={setKeyword}
            inputRef={inputRef}
            background="transparent"
            width="100%"
          />
        </Box>
        {permissions && (
          <Box
            component="button"
            onClick={handleDrawerOpen}
            sx={{
              padding: "10px 20px",
              backgroundColor: "#4b87ed",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": {
                backgroundColor: "#3a6fd8",
              },
            }}
          >
            + Add Note
          </Box>
        )}
      </Box>

      {/* Notes List */}
      {filteredNotes.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            padding: 6,
            color: "#9ca3af",
            fontSize: 14,
          }}
        >
          {keyword ? "No notes found matching your search" : "No notes available"}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {filteredNotes
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((note, index) => (
              <Box
                key={index}
                sx={{
                  backgroundColor: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: { xs: 2, md: 3 },
                  transition: "box-shadow 0.2s",
                  "&:hover": {
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: "#1f2937",
                        marginBottom: 1,
                      }}
                    >
                      {note.title}
                    </Box>
                    <Box
                      sx={{
                        fontSize: 14,
                        color: "#6b7280",
                        marginBottom: 1,
                        wordBreak: "break-word",
                      }}
                    >
                      {truncateText(note.notes, 100)}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        fontSize: 12,
                        color: "#9ca3af",
                      }}
                    >
                      <span>{formatDate(note.createdAt)}</span>
                      <span>•</span>
                      <span>By {note.createdBy}</span>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignSelf: { xs: "flex-end", md: "center" },
                    }}
                  >
                    <Box
                      component="img"
                      src={ViewIcon}
                      onClick={() => handleViewNote(note)}
                      sx={{
                        width: 24,
                        height: 24,
                        cursor: "pointer",
                        opacity: 0.7,
                        transition: "opacity 0.2s",
                        "&:hover": { opacity: 1 },
                      }}
                    />
                    {permissions && (
                      <>
                        <Box
                          component="img"
                          src={EditIcon}
                          onClick={() => handleEditButtonClick(note)}
                          sx={{
                            width: 24,
                            height: 24,
                            cursor: "pointer",
                            opacity: 0.7,
                            transition: "opacity 0.2s",
                            "&:hover": { opacity: 1 },
                          }}
                        />
                        <Box
                          component="img"
                          src={deleteIcon}
                          onClick={() => handleDeleteNote(note.id!)}
                          sx={{
                            width: 24,
                            height: 24,
                            cursor: "pointer",
                            opacity: 0.7,
                            transition: "opacity 0.2s",
                            "&:hover": { opacity: 1 },
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
        </Box>
      )}

      {/* Pagination */}
      {filteredNotes.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            marginTop: 3,
          }}
        >
          <Box
            component="button"
            onClick={goToPreviousPage}
            disabled={page === 0}
            sx={{
              px: 2,
              py: 1,
              border: "none",
              borderRadius: "6px",
              backgroundColor: page === 0 ? "#f3f4f6" : "#e5e7eb",
              color: page === 0 ? "#9ca3af" : "#374151",
              fontSize: 13,
              fontWeight: 500,
              cursor: page === 0 ? "not-allowed" : "pointer",
              "&:hover": page === 0 ? {} : { backgroundColor: "#d1d5db" },
            }}
          >
            ← Prev
          </Box>
          <Box sx={{ fontSize: 14, color: "#6b7280" }}>
            Page {page + 1} of {Math.ceil(filteredNotes.length / rowsPerPage) || 1}
          </Box>
          <Box
            component="button"
            onClick={goToNextPage}
            disabled={page >= Math.ceil(filteredNotes.length / rowsPerPage) - 1}
            sx={{
              px: 2,
              py: 1,
              border: "none",
              borderRadius: "6px",
              backgroundColor:
                page >= Math.ceil(filteredNotes.length / rowsPerPage) - 1
                  ? "#f3f4f6"
                  : "#e5e7eb",
              color:
                page >= Math.ceil(filteredNotes.length / rowsPerPage) - 1
                  ? "#9ca3af"
                  : "#374151",
              fontSize: 13,
              fontWeight: 500,
              cursor:
                page >= Math.ceil(filteredNotes.length / rowsPerPage) - 1
                  ? "not-allowed"
                  : "pointer",
              "&:hover":
                page >= Math.ceil(filteredNotes.length / rowsPerPage) - 1
                  ? {}
                  : { backgroundColor: "#d1d5db" },
            }}
          >
            Next →
          </Box>
        </Box>
      )}
    </Box>
  );

  const NotesDrawer = () => (
    <SideDrawer open={openDrawer} onClose={handleDrawerClose}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          px: { xs: 2, md: 3 },
          py: 3,
          backgroundColor: "#fafafa",
        }}
      >
        <SideDrawerHeader
          handleDrawerClose={handleDrawerClose}
          labelTitle={isEditMode ? "Edit Note" : "Add Note"}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flex: 1,
            mt: 3,
          }}
        >
          <Box>
            <input
              placeholder="Enter note title"
              onChange={(e) =>
                setCurrentNote((prev) => ({ ...prev, title: e.target.value }))
              }
              value={currentNote?.title || ""}
              required
              style={{
                width: "100%",
                height: 48,
                padding: "0 16px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                fontSize: 15,
                outline: "none",
                backgroundColor: "white",
                color: "#1f2937",
                transition: "border-color 0.2s, box-shadow 0.2s",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#4b87ed";
                e.target.style.boxShadow = "0 0 0 3px rgba(75, 135, 237, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            />
          </Box>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <textarea
              placeholder="Enter your note here..."
              style={{
                width: "100%",
                flex: 1,
                border: "1px solid #d1d5db",
                borderRadius: 6,
                fontSize: 15,
                outline: "none",
                backgroundColor: "white",
                color: "#1f2937",
                fontFamily: "inherit",
                resize: "none",
                padding: "14px 16px",
                transition: "border-color 0.2s, box-shadow 0.2s",
              }}
              onChange={(e) =>
                setCurrentNote((prev) => ({ ...prev, notes: e.target.value }))
              }
              value={currentNote?.notes || ""}
              required
              onFocus={(e) => {
                e.target.style.borderColor = "#4b87ed";
                e.target.style.boxShadow = "0 0 0 3px rgba(75, 135, 237, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "none";
              }}
            />
          </Box>
        </Box>
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Box
            onClick={handleSaveNote}
            sx={{
              backgroundColor: "#4b87ed",
              color: "white",
              padding: "12px 48px",
              borderRadius: "8px",
              fontSize: 15,
              fontWeight: 500,
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": {
                backgroundColor: "#3b72d7",
              },
              "&:active": {
                backgroundColor: "#2d5cb8",
              },
            }}
          >
            {isEditMode ? "Update" : "Create"}
          </Box>
        </Box>
      </Box>
    </SideDrawer>
  );

  const ViewNoteDrawer = () => (
    <SideDrawer open={openViewDrawer} onClose={handleViewDrawerClose}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          px: { xs: 2, md: 3 },
          py: 3,
          backgroundColor: "#fafafa",
        }}
      >
        <SideDrawerHeader
          handleDrawerClose={handleViewDrawerClose}
          labelTitle="View Note"
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            flex: 1,
            mt: 3,
            overflow: "auto",
          }}
        >
          {/* Title */}
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#6b7280",
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Title
            </Typography>
            <Box
              sx={{
                backgroundColor: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                padding: "12px 16px",
                fontSize: 15,
                color: "#1f2937",
                wordBreak: "break-word",
              }}
            >
              {viewRowDetailsData?.title || "N/A"}
            </Box>
          </Box>

          {/* Created Date */}
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#6b7280",
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Created Date
            </Typography>
            <Box
              sx={{
                backgroundColor: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                padding: "12px 16px",
                fontSize: 15,
                color: "#1f2937",
              }}
            >
              {viewRowDetailsData?.createdAt
                ? formatDate(viewRowDetailsData.createdAt)
                : "N/A"}
            </Box>
          </Box>

          {/* Note Content */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#6b7280",
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Note
            </Typography>
            <Box
              sx={{
                flex: 1,
                backgroundColor: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                padding: "14px 16px",
                fontSize: 15,
                color: "#1f2937",
                lineHeight: 1.6,
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                overflow: "auto",
              }}
            >
              {viewRowDetailsData?.notes || "N/A"}
            </Box>
          </Box>
        </Box>
      </Box>
    </SideDrawer>
  );

  return (
    <Box sx={{ width: "100%" }}>
      {NotesTable()}
      {NotesDrawer()}
      {ViewNoteDrawer()}
    </Box>
  );
};
